import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { FileText, Home } from 'lucide-react';
import { useLocation } from 'wouter';

const TermsPage: React.FC = () => {
  const [, setLocation] = useLocation();

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      <div className="container mx-auto px-4 py-8 max-w-4xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-4">
            <img 
              src="/app-logo.png" 
              alt="Tectonix" 
              className="h-12 w-12 mr-3 object-contain"
              style={{ filter: 'brightness(0)' }}
            />
            <h1 className="text-3xl font-bold text-gray-900">Tectonix</h1>
          </div>
          <div className="flex items-center justify-center gap-2 mb-2">
            <FileText className="h-6 w-6 text-blue-600" />
            <h2 className="text-2xl font-semibold text-gray-800">Terms of Service</h2>
          </div>
          <p className="text-sm text-gray-500">Last updated: {new Date().toLocaleDateString()}</p>
        </div>

        {/* Content */}
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle>Terms of Service</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6 prose max-w-none">
            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h3>
              <p className="text-gray-700 leading-relaxed">
                By accessing and using the Tectonix Procurement Management Platform ("the Platform"), 
                you accept and agree to be bound by these Terms of Service ("Terms"). If you do not agree 
                to these Terms, you must not use the Platform. These Terms apply to all users, including 
                those who access the Platform through Autodesk Construction Cloud (ACC) authentication or 
                tenderer access codes.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">2. Description of Service</h3>
              <p className="text-gray-700 leading-relaxed">
                Tectonix is a construction procurement management platform that integrates with Autodesk 
                Construction Cloud to provide:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2 mt-3">
                <li>Project and tender package management</li>
                <li>Document access and organization</li>
                <li>Multi-tier access control and permissions</li>
                <li>Analytics and reporting features</li>
                <li>Integration with Autodesk Construction Cloud services</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">3. User Accounts and Authentication</h3>
              <div className="space-y-3">
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">3.1 ACC Authentication</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Users may access the Platform using Autodesk Construction Cloud OAuth authentication. 
                    By using ACC authentication, you agree to comply with Autodesk's terms of service and 
                    privacy policy in addition to these Terms.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">3.2 Tenderer Access</h4>
                  <p className="text-gray-700 leading-relaxed">
                    Tenderer users may access the Platform using email and access codes provided by project administrators. 
                    You are responsible for maintaining the confidentiality of your access credentials.
                  </p>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">3.3 Account Security</h4>
                  <p className="text-gray-700 leading-relaxed">
                    You are responsible for all activities that occur under your account. You must immediately 
                    notify us of any unauthorized use of your account or any other breach of security.
                  </p>
                </div>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">4. Acceptable Use</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                You agree to use the Platform only for lawful purposes and in accordance with these Terms. 
                You agree not to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Violate any applicable laws or regulations</li>
                <li>Infringe upon the rights of others, including intellectual property rights</li>
                <li>Transmit any malicious code, viruses, or harmful software</li>
                <li>Attempt to gain unauthorized access to the Platform or related systems</li>
                <li>Interfere with or disrupt the Platform's operation or security</li>
                <li>Use the Platform to send spam or unsolicited communications</li>
                <li>Impersonate any person or entity or misrepresent your affiliation</li>
                <li>Collect or harvest information about other users without consent</li>
                <li>Use automated systems to access the Platform without authorization</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">5. Intellectual Property</h3>
              <div className="space-y-3">
                <p className="text-gray-700 leading-relaxed">
                  The Platform and its content, features, and functionality are owned by Tectonix and its licensors 
                  and are protected by international copyright, trademark, and other intellectual property laws.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  You retain ownership of any content you upload or submit through the Platform. By using the Platform, 
                  you grant us a license to use, store, and process your content as necessary to provide the service.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  Content accessed through Autodesk Construction Cloud integration remains subject to Autodesk's 
                  terms and your project agreements.
                </p>
              </div>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">6. Access Control and Permissions</h3>
              <p className="text-gray-700 leading-relaxed">
                The Platform implements role-based access control. Your access level determines what features and 
                data you can view or modify. You agree to:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2 mt-3">
                <li>Respect access restrictions and not attempt to bypass permission controls</li>
                <li>Not share your access credentials with unauthorized persons</li>
                <li>Use the Platform only for authorized business purposes</li>
                <li>Comply with project-specific access rules and guidelines</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">7. Data and Privacy</h3>
              <p className="text-gray-700 leading-relaxed">
                Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these 
                Terms by reference. By using the Platform, you consent to the collection and use of information as 
                described in the Privacy Policy.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">8. Service Availability</h3>
              <p className="text-gray-700 leading-relaxed">
                We strive to maintain high availability of the Platform but do not guarantee uninterrupted or error-free 
                service. The Platform may be temporarily unavailable due to maintenance, updates, or circumstances beyond 
                our control. We reserve the right to modify, suspend, or discontinue any part of the Platform at any time.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">9. Third-Party Services</h3>
              <p className="text-gray-700 leading-relaxed">
                The Platform integrates with Autodesk Construction Cloud and other third-party services. Your use of 
                these services is subject to their respective terms of service and privacy policies. We are not responsible 
                for the availability, content, or practices of third-party services.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">10. Disclaimers</h3>
              <p className="text-gray-700 leading-relaxed mb-3">
                THE PLATFORM IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR 
                IMPLIED, INCLUDING BUT NOT LIMITED TO:
              </p>
              <ul className="list-disc list-inside text-gray-700 ml-4 space-y-2">
                <li>Warranties of merchantability, fitness for a particular purpose, or non-infringement</li>
                <li>Warranties that the Platform will be uninterrupted, secure, or error-free</li>
                <li>Warranties regarding the accuracy, reliability, or completeness of any content</li>
              </ul>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">11. Limitation of Liability</h3>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, TECTONIX AND ITS AFFILIATES SHALL NOT BE LIABLE FOR ANY INDIRECT, 
                INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER 
                INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING 
                FROM YOUR USE OF THE PLATFORM.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">12. Indemnification</h3>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify, defend, and hold harmless Tectonix and its officers, directors, employees, and agents 
                from any claims, damages, losses, liabilities, and expenses (including legal fees) arising from your use of 
                the Platform, violation of these Terms, or infringement of any rights of another.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">13. Termination</h3>
              <p className="text-gray-700 leading-relaxed">
                We may terminate or suspend your access to the Platform immediately, without prior notice, for any breach of 
                these Terms or for any other reason we deem necessary. Upon termination, your right to use the Platform will 
                cease immediately. You may also terminate your account at any time by contacting us.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">14. Changes to Terms</h3>
              <p className="text-gray-700 leading-relaxed">
                We reserve the right to modify these Terms at any time. We will notify users of material changes by posting 
                the updated Terms on this page and updating the "Last updated" date. Your continued use of the Platform after 
                such changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">15. Governing Law</h3>
              <p className="text-gray-700 leading-relaxed">
                These Terms shall be governed by and construed in accordance with applicable laws, without regard to conflict 
                of law principles. Any disputes arising from these Terms or your use of the Platform shall be subject to the 
                exclusive jurisdiction of the appropriate courts.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">16. Severability</h3>
              <p className="text-gray-700 leading-relaxed">
                If any provision of these Terms is found to be unenforceable or invalid, that provision shall be limited or 
                eliminated to the minimum extent necessary, and the remaining provisions shall remain in full force and effect.
              </p>
            </section>

            <section>
              <h3 className="text-xl font-semibold text-gray-900 mb-3">17. Contact Information</h3>
              <p className="text-gray-700 leading-relaxed">
                If you have questions about these Terms of Service, please contact us at:
              </p>
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-800 font-semibold">Tectonix Procurement Platform</p>
                <p className="text-gray-700">Email: <a href="mailto:admin@moatcity.com" className="text-blue-600 hover:text-blue-800 underline">admin@moatcity.com</a></p>
              </div>
            </section>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center">
          <button
            onClick={() => setLocation('/')}
            className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
          >
            <Home className="h-4 w-4" />
            Return to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsPage;

